import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuTrash2 } from 'react-icons/lu';

import Modal from '../../../../components/Modal/Modal';
import {
   createDunningStage,
   deleteDunningStage,
   updateDunningStage,
} from '../../../../services/finance/dunningApi';
import { ControlInput, FieldHint, Pill } from '../../../../styles/dunning/base';
import {
   StageAddButton,
   StageAutoToggle,
   StageDeleteButton,
   StageHint,
   StageInfo,
   StageList,
   StageName,
   StageNewRow,
   StagePosition,
   StageRow,
} from '../../../../styles/dunning/modals';
import {
   STAGE_TYPE_HINTS,
   STAGE_TYPE_LABELS,
   STAGE_TYPE_PILL_VARIANTS,
} from '../constants';
import { financeErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// Gestão de raias (finance:dunning_stage:manage — o botão que abre este modal
// nem existe sem a permissão). Só raia ATTEMPT pode ser criada, excluída ou
// ter o avanço automático alternado; ENTRY/TERMINAL_PAID/EXTERNAL são
// protegidas (guia §5.5). Cada bloqueio tem `title` explicando o porquê.
//
// `occupiedStageIds` vem da página corrente da lista — sinal parcial (com
// total_pages > 1 o backend continua sendo o guard real, via 422
// dunning_stage_occupied, que vira toast do dicionário).
// ---------------------------------------------------------------------------

function stageHint(stage) {
   if (stage.stage_type === 'ATTEMPT') {
      return stage.auto_advance_on_due
         ? 'Avanço automático às 06:00 quando o compromisso vence'
         : 'Avanço manual';
   }
   return STAGE_TYPE_HINTS[stage.stage_type] ?? '';
}

export default function StagesModal({
   isOpen,
   stages,
   occupiedStageIds,
   onClose,
   onChanged,
}) {
   const [newName, setNewName] = useState('');
   const [busyStageId, setBusyStageId] = useState(null);
   const [isAdding, setIsAdding] = useState(false);

   const sorted = [...stages].sort((a, b) => a.position - b.position);
   const attemptCount = sorted.filter(
      (stage) => stage.stage_type === 'ATTEMPT'
   ).length;

   const handleToggleAuto = async (stage) => {
      setBusyStageId(stage.id);
      try {
         await updateDunningStage(stage.id, {
            autoAdvanceOnDue: !stage.auto_advance_on_due,
         });
         onChanged();
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível alterar a raia.')
         );
      } finally {
         setBusyStageId(null);
      }
   };

   const handleDelete = async (stage) => {
      setBusyStageId(stage.id);
      try {
         await deleteDunningStage(stage.id);
         toast.success('Raia excluída.');
         onChanged();
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível excluir a raia.')
         );
      } finally {
         setBusyStageId(null);
      }
   };

   const handleAdd = async () => {
      const name = newName.trim();
      if (!name) return;
      setIsAdding(true);
      try {
         await createDunningStage({ name });
         toast.success('Raia criada.');
         setNewName('');
         onChanged();
      } catch (err) {
         toast.error(
            financeErrorMessage(err, 'Não foi possível criar a raia.')
         );
      } finally {
         setIsAdding(false);
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="Raias do kanban"
         onClose={onClose}
         maxWidth="640px"
      >
         <StageList>
            {sorted.map((stage) => {
               const isAttempt = stage.stage_type === 'ATTEMPT';
               const isOccupied = occupiedStageIds.has(stage.id);
               const isLastAttempt = isAttempt && attemptCount === 1;
               const deleteDisabled =
                  !isAttempt ||
                  isOccupied ||
                  isLastAttempt ||
                  busyStageId === stage.id;

               let deleteTitle = 'Excluir raia';
               if (!isAttempt) deleteTitle = 'Raia protegida';
               else if (isOccupied) deleteTitle = 'Raia com casos ativos';
               else if (isLastAttempt) {
                  deleteTitle = 'Deve existir ao menos uma tentativa';
               }

               return (
                  <StageRow key={stage.id}>
                     <StagePosition>{stage.position}</StagePosition>
                     <StageInfo>
                        <StageName>{stage.name}</StageName>
                        <StageHint>{stageHint(stage)}</StageHint>
                     </StageInfo>
                     <Pill
                        $variant={
                           STAGE_TYPE_PILL_VARIANTS[stage.stage_type] ??
                           'neutral'
                        }
                     >
                        {STAGE_TYPE_LABELS[stage.stage_type] ?? '—'}
                     </Pill>
                     <StageAutoToggle
                        type="button"
                        $on={stage.auto_advance_on_due}
                        disabled={!isAttempt || busyStageId === stage.id}
                        title={
                           isAttempt
                              ? 'Alternar avanço automático'
                              : 'Disponível apenas em raias de tentativa'
                        }
                        onClick={() => handleToggleAuto(stage)}
                     >
                        {stage.auto_advance_on_due ? 'Auto' : 'Manual'}
                     </StageAutoToggle>
                     <StageDeleteButton
                        type="button"
                        disabled={deleteDisabled}
                        title={deleteTitle}
                        onClick={() => handleDelete(stage)}
                     >
                        <LuTrash2 size={15} />
                     </StageDeleteButton>
                  </StageRow>
               );
            })}
         </StageList>

         <StageNewRow>
            <ControlInput
               value={newName}
               placeholder="Nome da nova raia"
               maxLength={60}
               onChange={(event) => setNewName(event.target.value)}
               onKeyDown={(event) => {
                  if (event.key === 'Enter') handleAdd();
               }}
            />
            <StageAddButton
               type="button"
               onClick={handleAdd}
               disabled={isAdding || !newName.trim()}
            >
               + Nova raia
            </StageAddButton>
         </StageNewRow>
         <FieldHint>
            Raias novas são sempre do tipo tentativa. Entrada, Recebido e
            Cobrança administrativa são protegidas.
         </FieldHint>
      </Modal>
   );
}

StagesModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   stages: PropTypes.array.isRequired,
   occupiedStageIds: PropTypes.instanceOf(Set).isRequired,
   onClose: PropTypes.func.isRequired,
   onChanged: PropTypes.func.isRequired,
};
